import axios from 'axios'
import { supabase } from './supabase'
import { getApiBaseUrl } from '../config/runtime-config'

// Create axios instance with runtime-evaluated base URL
export const api = axios.create({
  baseURL: getApiBaseUrl(),
})

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  
  // Mobile-specific headers to prevent CORS issues
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  if (isMobile) {
    // Ensure content-type is set for POST requests
    if (config.method === 'post' && !config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }
    
    // Log request for debugging
    console.log('[Mobile] API Request:', {
      url: config.url,
      method: config.method,
      headers: config.headers,
    });
  }
  
  return config
}, (error) => {
  console.error('[Mobile] Request interceptor error:', error);
  return Promise.reject(error);
})

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      console.log('[Mobile] API Response:', {
        url: response.config.url,
        status: response.status,
      });
    }
    return response;
  },
  (error) => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      console.error('[Mobile] API Error:', {
        url: error.config?.url,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
    }
    return Promise.reject(error);
  }
)

// API methods
export const chatAPI = {
  sendMessage: async (messages: any[], conversationId?: string) => {
    const response = await api.post('/api/chat/', {
      messages,
      conversation_id: conversationId,
      use_memory: true,
    })
    return response.data
  },

  getHistory: async (conversationId?: string, limit = 50) => {
    const params = new URLSearchParams()
    if (conversationId) params.append('conversation_id', conversationId)
    params.append('limit', limit.toString())
    
    const response = await api.get(`/api/chat/history?${params}`)
    return response.data
  },
}

export const searchAPI = {
  search: async (query: string, type = 'hybrid', limit = 10) => {
    const response = await api.post('/api/search', {
      query,
      search_type: type,
      limit,
    })
    return response.data
  },

  getSuggestions: async (query: string) => {
    const response = await api.get(`/api/search/suggestions?q=${encodeURIComponent(query)}`)
    return response.data
  },

  getPopular: async () => {
    const response = await api.get('/api/search/popular')
    return response.data
  },
}

export const ingestAPI = {
  uploadFiles: async (files: File[], title?: string, metadata?: any) => {
    const formData = new FormData()
    files.forEach(file => formData.append('files', file))
    if (title) formData.append('title', title)
    if (metadata) formData.append('metadata', JSON.stringify(metadata))
    
    const response = await api.post('/api/ingest/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  uploadText: async (title: string, content: string, metadata?: any) => {
    const formData = new FormData()
    formData.append('title', title)
    formData.append('content', content)
    if (metadata) formData.append('metadata', JSON.stringify(metadata))
    
    const response = await api.post('/api/ingest/text', formData)
    return response.data
  },

  uploadVoice: async (audioFile: File, title?: string, language = 'de') => {
    const formData = new FormData()
    formData.append('audio_file', audioFile)
    if (title) formData.append('title', title)
    formData.append('language', language)
    
    const response = await api.post('/api/ingest/voice', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  getStatus: async (documentId: string) => {
    const response = await api.get(`/api/ingest/status/${documentId}`)
    return response.data
  },

  getDocuments: async () => {
    const response = await api.get('/api/ingest/documents')
    return response.data
  },

  deleteDocument: async (documentId: string) => {
    const response = await api.delete(`/api/ingest/${documentId}`)
    return response.data
  },
  
  getReferenceDocuments: async () => {
    const response = await api.get('/api/reference-documents')
    return response.data
  },
  
  uploadReferenceDocument: async (files: File[], referenceType: string, description?: string) => {
    const formData = new FormData()
    files.forEach(file => formData.append('files', file))
    formData.append('reference_type', referenceType)
    if (description) formData.append('description', description)
    
    const response = await api.post('/api/reference-documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },
  
  deleteReferenceDocument: async (documentId: string) => {
    const response = await api.delete(`/api/reference-documents/${documentId}`)
    return response.data
  },
}

export const feedbackAPI = {
  reportKnowledgeGap: async (question: string, context?: string, priority = 3) => {
    const response = await api.post('/api/feedback/knowledge-gap', {
      question,
      context,
      priority,
    })
    return response.data
  },

  getKnowledgeGaps: async (status?: string) => {
    const params = new URLSearchParams()
    if (status) params.append('status', status)
    
    const response = await api.get(`/api/feedback/knowledge-gaps?${params}`)
    return response.data
  },

  resolveKnowledgeGap: async (gapId: string, response: string) => {
    const res = await api.put(`/api/feedback/knowledge-gap/${gapId}/resolve`, { response })
    return res.data
  },

  getStats: async () => {
    const response = await api.get('/api/feedback/stats')
    return response.data
  },
}

export const extractionV2API = {
  cleanContent: async (documentId: string) => {
    const response = await api.post('/api/extraction-v2/clean', {
      document_id: documentId
    })
    return response.data
  },

  extractChunks: async (documentId: string, maxChunkTokens = 1000) => {
    const response = await api.post('/api/extraction-v2/chunk', {
      document_id: documentId,
      max_chunk_tokens: maxChunkTokens
    })
    return response.data
  },

  processComplete: async (documentId: string) => {
    const response = await api.post('/api/extraction-v2/process-complete', {
      document_id: documentId
    })
    return response.data
  },

  getStatus: async (documentId: string) => {
    const response = await api.get(`/api/extraction-v2/status/${documentId}`)
    return response.data
  },
}

export const chunkReviewAPI = {
  startExtraction: async (documentId: string, extractionType = 'smart_chunking') => {
    const response = await api.post(`/api/chunk-review/extract/${documentId}`, {
      extraction_type: extractionType
    })
    return response.data
  },

  getProposals: async (documentId: string, status?: string) => {
    const params = new URLSearchParams()
    if (status) params.append('status', status)
    
    const response = await api.get(`/api/chunk-review/proposals/${documentId}?${params}`)
    return response.data
  },

  reviewProposal: async (proposalId: string, review: any) => {
    const response = await api.patch(`/api/chunk-review/proposals/${proposalId}`, review)
    return response.data
  },

  bulkReview: async (documentId: string, action: 'approve_all' | 'reject_all') => {
    const response = await api.post(`/api/chunk-review/proposals/${documentId}/bulk-review?action=${action}`)
    return response.data
  },

  getStats: async (documentId: string) => {
    const response = await api.get(`/api/chunk-review/stats/${documentId}`)
    return response.data
  },

  applyChunks: async (documentId: string) => {
    const response = await api.post(`/api/chunk-review/apply/${documentId}`)
    return response.data
  },
}

export const memoryAPI = {
  getMemories: async (query?: string, limit = 20) => {
    const params = new URLSearchParams()
    if (query) params.append('query', query)
    params.append('limit', limit.toString())
    
    const response = await api.get(`/api/memory/memories?${params}`)
    return response.data
  },

  getConversations: async (limit = 10) => {
    const response = await api.get(`/api/memory/conversations?limit=${limit}`)
    return response.data
  },

  getConversationMessages: async (conversationId: string) => {
    const response = await api.get(`/api/memory/conversation/${conversationId}/messages`)
    return response.data
  },

  updatePreference: async (key: string, value: any) => {
    const response = await api.post('/api/memory/preferences', { key, value })
    return response.data
  },

  getPreferences: async () => {
    const response = await api.get('/api/memory/preferences')
    return response.data
  },
}

export const chatPromptsAPI = {
  getPrompts: async () => {
    const response = await api.get('/api/chat-prompts')
    return response.data
  },

  getAllPrompts: async () => {
    const response = await api.get('/api/chat-prompts/all')
    return response.data
  },

  updatePrompt: async (position: number, data: { prompt_text: string; is_active: boolean }) => {
    const response = await api.put(`/api/chat-prompts/${position}`, data)
    return response.data
  },

  resetPrompts: async () => {
    const response = await api.post('/api/chat-prompts/reset')
    return response.data
  },
}

export const usersAPI = {
  getUsers: async (limit = 100, offset = 0) => {
    const response = await api.get(`/api/users?limit=${limit}&offset=${offset}`)
    return response.data
  },

  updateUserRole: async (userId: string, data: { role: string }) => {
    const response = await api.patch(`/api/users/${userId}/role`, data)
    return response.data
  },

  deleteUser: async (userId: string) => {
    const response = await api.delete(`/api/users/${userId}`)
    return response.data
  },

  getCurrentUser: async () => {
    const response = await api.get('/api/users/me')
    return response.data
  },

  initSuperUser: async () => {
    const response = await api.post('/api/users/init-super-user')
    return response.data
  },
}

export default api